import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { naira, formatDate, formatApiError, statusBadgeClass, statusLabel } from "../lib/format";
import { Card, Loading, EmptyState } from "../components/UI";
import { Zap, Plus, X, Users as UsersIcon, Pencil } from "lucide-react";

const CATEGORIES = [
  {v:"electricity",l:"Electricity"},{v:"water",l:"Water"},{v:"security",l:"Security Levy"},
  {v:"waste",l:"Waste Disposal"},{v:"estate",l:"Estate Charges"},{v:"other",l:"Other"}
];

const STATUSES = ["pending", "paid", "overdue", "cancelled"];

export default function UtilityBills() {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assignedTo, setAssignedTo] = useState("all");
  const [selectedTenants, setSelectedTenants] = useState([]);

  const reload = async () => {
    const [b, t] = await Promise.all([
      api.get("/utility-bills"),
      api.get("/tenants").catch(()=>({data:[]})),
    ]);
    setItems(b.data); setTenants(t.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post("/utility-bills", {
        category: f.get("category"),
        description: f.get("description"),
        amount: Number(f.get("amount")),
        billing_period: f.get("period"),
        assigned_to: assignedTo,
        tenant_ids: assignedTo === "multiple" ? selectedTenants : (assignedTo === "one" ? [f.get("tenant_id")] : []),
        split_equally: f.get("split") === "on",
        due_date: f.get("due_date"),
      });
      toast.success("Bill created and invoices generated for tenants");
      setCreating(false); setSelectedTenants([]);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const payload = {
      amount: Number(f.get("amount")),
      due_date: f.get("due_date") || null,
      status: f.get("status"),
      notes: f.get("notes") || "",
    };
    try {
      await api.patch(`/utility-bills/${editing.id}`, payload);
      toast.success("Bill updated");
      setEditing(null);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  const total = items.reduce((s, b) => s + (b.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Utility Bills & Service Charges</h1>
          <p className="text-[#5E7166] text-sm">Assign costs to one, multiple, or all tenants.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-gold" data-testid="new-bill-btn"><Plus size={16}/> New Bill</button>
      </div>

      <Card className="!p-0 overflow-hidden">
        {items.length === 0 ? <EmptyState icon={Zap} title="No bills yet" description="Add utility/service charges and assign to tenants."/> :
          <div className="overflow-x-auto">
            <table className="modav-table">
              <thead><tr><th>Category</th><th>Period</th><th>Assigned To</th><th>Tenants</th><th>Amount</th><th>Due</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {items.map(b => (
                  <tr key={b.id} data-testid={`bill-${b.id}`}>
                    <td><span className="badge badge-blue">{b.category}</span><div className="text-xs text-[#5E7166] line-clamp-1 mt-1">{b.description}</div></td>
                    <td className="text-xs">{b.billing_period}</td>
                    <td>{statusLabel(b.assigned_to)}</td>
                    <td>{b.tenant_ids?.length || 0}</td>
                    <td className="font-serif font-bold text-[#0E5C3A]">{naira(b.amount)}</td>
                    <td className="text-xs">{formatDate(b.due_date)}</td>
                    <td><span className={statusBadgeClass(b.status)}>{statusLabel(b.status)}</span></td>
                    <td><button onClick={() => setEditing(b)} title="Edit bill" className="text-[#0E5C3A] hover:bg-[#E8F5EE] p-1.5 rounded" data-testid={`edit-bill-${b.id}`}><Pencil size={14}/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </Card>

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="bill-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">New Utility / Service Charge</h3>
              <button onClick={() => setCreating(false)}><X size={20}/></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Category</span>
                  <select name="category" required className="input-modav mt-1">{CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}</select></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Amount Total</span>
                  <input type="number" name="amount" required className="input-modav mt-1"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Billing Period</span>
                  <input name="period" required className="input-modav mt-1" placeholder="Mar 2026"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Due Date</span>
                  <input type="date" name="due_date" required className="input-modav mt-1"/></label>
              </div>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Description</span>
                <textarea name="description" rows={2} className="input-modav mt-1"/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Assign To</span>
                <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)} className="input-modav mt-1">
                  <option value="all">All active tenants</option><option value="multiple">Multiple tenants</option><option value="one">One tenant</option>
                </select></label>
              {assignedTo === "one" && (
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Tenant</span>
                  <select name="tenant_id" required className="input-modav mt-1">
                    <option value="">—</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                  </select></label>
              )}
              {assignedTo === "multiple" && (
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Tenants ({selectedTenants.length} selected)</span>
                  <div className="mt-1 max-h-40 overflow-y-auto border border-[#E5EBE8] rounded-lg p-2 space-y-1">
                    {tenants.map(t => (
                      <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#F7F9F8] p-1 rounded">
                        <input type="checkbox" checked={selectedTenants.includes(t.id)} onChange={e => {
                          if (e.target.checked) setSelectedTenants([...selectedTenants, t.id]);
                          else setSelectedTenants(selectedTenants.filter(x => x !== t.id));
                        }}/>
                        {t.full_name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="split" defaultChecked/> Split amount equally among tenants</label>
              <button type="submit" className="btn-gold w-full justify-center" data-testid="save-bill-btn">Create Bill</button>
            </form>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-md" data-testid="edit-bill-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Edit Utility Bill</h3>
              <button onClick={() => setEditing(null)}><X size={20}/></button>
            </div>
            <p className="text-xs text-[#5E7166] mb-3"><span className="font-semibold capitalize">{editing.category}</span> · {editing.billing_period}</p>
            <form onSubmit={saveEdit} className="space-y-3" data-testid="edit-bill-form">
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Amount (₦)</span>
                <input name="amount" type="number" min="0" required defaultValue={editing.amount} className="input-modav mt-1" data-testid="edit-bill-amount"/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Due date</span>
                <input name="due_date" type="date" defaultValue={editing.due_date ? String(editing.due_date).slice(0,10) : ""} className="input-modav mt-1" data-testid="edit-bill-due"/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Status</span>
                <select name="status" required defaultValue={editing.status || "pending"} className="input-modav mt-1" data-testid="edit-bill-status">
                  {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                </select></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Notes</span>
                <textarea name="notes" rows={2} defaultValue={editing.notes || ""} className="input-modav mt-1"/></label>
              <p className="text-[10px] text-[#5E7166]">Linked tenant invoice rows are updated automatically.</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditing(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
                <button type="submit" className="btn-gold flex-1 justify-center" data-testid="save-edit-bill-btn">Save changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
